import { apiSlice } from "../../../features/api/apiSlice";

/**
 * Portal settings — the values the secretariat changes without a deploy.
 *
 * The server owns the catalogue: which settings exist, what each is called,
 * how to read it back and what it falls back to. This layer carries it across
 * unchanged and never keeps a second copy — a portal that decides for itself
 * what a setting means would eventually disagree with the code that reads it.
 */

export type SettingType = "string" | "integer" | "decimal" | "boolean";

export type PortalSetting = {
  key: string;
  label: string;
  description?: string | null;
  type: SettingType;
  /** "naira" renders and is entered in naira; "points" / "years" label only. */
  unit?: string | null;
  /** Shown, but written by the portal itself — the series counters. */
  readonly: boolean;
  value: string | number | null;
  default: string | number | null;
  /** Whether the value in force was typed in here, or is the shipped default. */
  source: "settings" | "default";
};

export type PortalSettingGroup = {
  group: string;
  label: string;
  description?: string | null;
  settings: PortalSetting[];
};

export type SeriesState = {
  series: string;
  series_label: string;
  prefix: string;
  /** What goes after the number — a year, a separator. Often empty. */
  postfix: string;
  width: number;
  highest: number | null;
  highest_text: string | null;
  next: number;
  next_text: string;
  register_highest: number | null;
  recorded_last: number | null;
  seeded_from: "register" | "settings" | "configured_start";
  floor: number;
};

export const portalSettingsApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    portalSettings: builder.query<any, void>({
      query: () => "/admin/settings",
      providesTags: ["PortalSettings"],
    }),

    savePortalSettings: builder.mutation<
      any,
      { settings: { key: string; value: string | number | null }[] }
    >({
      query: (body) => ({
        // POST, not PUT — the host blocks the less common verbs.
        url: "/admin/settings",
        method: "POST",
        body,
      }),
      invalidatesTags: ["PortalSettings"],
    }),
  }),
  overrideExisting: false,
});

export const { usePortalSettingsQuery, useSavePortalSettingsMutation } = portalSettingsApi;
